import { Node } from '@tiptap/core'
import { getEmbedAttributes, handleClickNode, getPasteRules, deleteNode, sanitizeEmbedInput } from '@/vendors/tiptap/helpers'
import { useNotificationStore } from '@/stores/NotificationStore'
import { useVendors } from '@/composables/app/useVendors'
import { get as _get } from 'lodash'

export default Node.create({
  name: 'embed',
  group: 'block',
  selectable: true,
  draggable: true,
  priority: 1000,

  addOptions () {
    return {
      toggle: false
    }
  },

  addAttributes () {
    return {
      type: {
        parseHTML: element => {
          const { isEmbedVideoUrl } = useVendors()

          const src = element.querySelector('iframe')?.getAttribute('src')

          return isEmbedVideoUrl(src) ? 'video' : 'code'
        },
        rendered: false
      },
      input: {
        default: null,
        parseHTML: element => {
          const { isEmbedVideoUrl } = useVendors()

          const src = element.querySelector('iframe')?.getAttribute('src')

          return isEmbedVideoUrl(src) ? src : sanitizeEmbedInput(element.innerHTML)
        },
        rendered: false
      },
      src: {
        default: null,
        parseHTML: element => element.querySelector('iframe')?.getAttribute('src'),
        rendered: false
      },
      width: {
        default: 630,
        parseHTML: element => element.querySelector('iframe')?.getAttribute('width'),
        rendered: false
      },
      height: {
        default: 354,
        parseHTML: element => element.querySelector('iframe')?.getAttribute('height'),
        rendered: false
      },
      frameborder: {
        default: 0,
        parseHTML: element => element.querySelector('iframe')?.getAttribute('frameborder'),
        rendered: false
      },
      allowfullscreen: {
        default: 0,
        parseHTML: element => element.querySelector('iframe')?.getAttribute('allowfullscreen'),
        rendered: false
      },
      allow: {
        default: null,
        parseHTML: element => element.querySelector('iframe')?.getAttribute('allow'),
        rendered: false
      }
    }
  },

  parseHTML () {
    return [
      {
        tag: 'object',
        getAttrs: element => {
          return element.querySelector('iframe')?.getAttribute('src') ? {} : false
        }
      }
    ]
  },

  renderHTML ({ node, HTMLAttributes }) {
    return ['object', HTMLAttributes, [
      'iframe', {
        src: _get(node, 'attrs.src'),
        width: _get(node, 'attrs.width'),
        height: _get(node, 'attrs.height'),
        frameborder: _get(node, 'attrs.frameborder'),
        allowfullscreen: _get(node, 'attrs.allowfullscreen'),
        allow: _get(node, 'attrs.allow')
      }
    ]]
  },

  addCommands () {
    return {
      setEmbed: attributes => ({ tr, commands, chain }) => {
        const { postSideNotification } = useNotificationStore()
        const { isVendorAllowed } = useVendors()

        if (attributes.input) {
          const attrs = getEmbedAttributes(attributes)

          if (!isVendorAllowed(attrs.src)) return postSideNotification({ text: 'errors.iframe_is_not_allowed', type: 'error', delay: 10000 })

          chain()
            .insertContentAt(tr.selection, [
              { type: this.name, attrs },
              { type: 'paragraph' }
            ])
            .run()
        } else {
          commands.unsetEmbed()
        }
      },
      unsetEmbed: () => props => deleteNode({ props, name: this.name })
    }
  },

  addPasteRules () {
    const { video_vendors, isEmbedVideoUrl } = useVendors()

    return getPasteRules({
      type: this.type,
      expressions: [...video_vendors.map(({ name }) => name ), 'iframe'],
      handler: ({ chain, input }) => chain.setEmbed({ input, type: isEmbedVideoUrl(input) ? 'video' : 'code' })
    })
  },

  addProseMirrorPlugins () {
    const plugins = []

    if (this.options.toggle) {
      plugins.push(handleClickNode({
        tag: 'object',
        type: this.type,
        toggle: this.options.toggle
      }))
    }

    return plugins
  }
})